import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Maximize2 } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Reveal, ScaleImage } from "@/components/site/Reveal";
import Lightbox from "@/components/site/Lightbox";
import { PROJECT_GROUPS, ADDITIONAL_NOTE, ADDITIONAL_IMAGES } from "@/data/projects";

const SECTION_ORDER = ["marine", "defence", "steel"];
const ORDERED_GROUPS = [...PROJECT_GROUPS].sort(
  (a, b) => SECTION_ORDER.indexOf(a.id) - SECTION_ORDER.indexOf(b.id)
);

export default function Industries() {
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null); // { images, title, client, index }
  const location = useLocation();
  const groups = filter === "all" ? ORDERED_GROUPS : ORDERED_GROUPS.filter((g) => g.id === filter);

  const openProject = (p) => setLightbox({ images: p.images, title: p.title, client: p.client, index: 0 });

  // Deep-link: scroll to a sector section when arriving with a hash (e.g. /industries#defence)
  useEffect(() => {
    const id = location.hash ? location.hash.replace("#", "") : "";
    if (!id) return;
    setFilter("all");
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -140 });
      else el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [location.key, location.hash]);

  return (
    <div data-testid="industries-page">
      <PageHero
        index="/ 04"
        overline="Industries & Work"
        title={<>Proven across the<br />hardest programmes.</>}
        subtitle="A portfolio spanning defence, marine & offshore, and steel & heavy engineering — every client and project drawn from delivered work."
      />

      {/* Filter */}
      <div className="sticky top-[88px] z-40 bg-white/85 backdrop-blur-xl border-b border-border">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 flex gap-2 overflow-x-auto py-4">
          {[{ id: "all", industry: "All Work" }, ...ORDERED_GROUPS].map((g) => (
            <button
              key={g.id}
              data-testid={`industry-filter-${g.id}`}
              onClick={() => setFilter(g.id)}
              className={`whitespace-nowrap px-5 py-2.5 text-[12px] font-medium uppercase tracking-wider transition-colors ${
                filter === g.id
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground border border-border"
              }`}
            >
              {g.industry}
            </button>
          ))}
        </div>
      </div>

      {groups.map((group) => (
        <section key={group.id} id={group.id} className="py-14 md:py-20 border-b border-border scroll-mt-[140px]" data-testid={`industry-section-${group.id}`}>
          <div className="mx-auto max-w-[1500px] px-6 md:px-12">
            <Reveal>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-14">
                <div className="lg:col-span-7">
                  <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--signal))]">
                    Sector
                  </span>
                  <h2 className="mt-3 font-display font-medium text-4xl md:text-6xl tracking-tight leading-[1]">
                    {group.industry}
                  </h2>
                </div>
                <p className="lg:col-span-5 self-end text-muted-foreground leading-relaxed">{group.intro}</p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {group.projects.map((p, i) => (
                <Reveal key={p.title} delay={(i % 3) * 0.08}>
                  <article className="group" data-testid={`project-card-${group.id}-${i}`}>
                    <button
                      type="button"
                      onClick={() => openProject(p)}
                      data-testid={`project-open-${group.id}-${i}`}
                      aria-label={`View ${p.title} gallery`}
                      className="relative block w-full text-left cursor-pointer"
                    >
                      <ScaleImage src={p.images[0]} alt={p.title} className="h-[280px] md:h-[320px]" />
                      <span className="absolute top-4 left-4 font-mono-tech text-[10px] uppercase tracking-widest text-white/90 bg-black/45 backdrop-blur px-2 py-1 z-10">
                        {p.client}
                      </span>
                      {/* hover: view more (bottom-right) */}
                      <span className="absolute bottom-4 right-4 z-10 flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest text-white bg-black/55 backdrop-blur border border-white/40 px-3 py-2 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                        <Maximize2 className="h-3.5 w-3.5" /> View more
                      </span>
                    </button>
                    <div className="pt-5">
                      <h3 className="font-display font-medium text-xl md:text-2xl tracking-tight">{p.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.detail}</p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Additional */}
      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
              <h2 className="lg:col-span-6 font-display font-medium text-3xl md:text-4xl tracking-tight">
                Additional precision components
              </h2>
              <p className="lg:col-span-6 self-end text-muted-foreground leading-relaxed">{ADDITIONAL_NOTE}</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {ADDITIONAL_IMAGES.map((src, i) => (
              <Reveal key={src} delay={i * 0.08}>
                <button
                  type="button"
                  onClick={() => setLightbox({ images: ADDITIONAL_IMAGES, title: "Additional precision components", client: "", index: i })}
                  data-testid={`additional-image-${i}`}
                  aria-label="View precision component"
                  className="group relative block w-full text-left cursor-pointer"
                >
                  <ScaleImage src={src} alt="Precision component" className="h-64 md:h-72" />
                  <span className="absolute bottom-4 right-4 z-10 grid place-items-center h-9 w-9 text-white bg-black/55 backdrop-blur border border-white/40 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    <Maximize2 className="h-3.5 w-3.5" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Lightbox
        open={!!lightbox}
        images={lightbox?.images || []}
        index={lightbox?.index || 0}
        title={lightbox?.title}
        client={lightbox?.client}
        onClose={() => setLightbox(null)}
        onIndexChange={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
      />
    </div>
  );
}
